import { useState, useCallback, FC } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/shared/ui/dialog';
import { Button } from '@/shared/ui/button';
import { Badge } from '@/shared/ui/badge';
import {
  ClipboardList,
  Eye,
  HelpCircle,
  Plus,
  Trash2,
  Loader2,
  ChevronDown,
  ChevronUp,
  Calendar,
  User,
  AlertCircle,
  ExternalLink,
  Lock,
  MapPin,
  Tag,
  Wrench,
  ShieldAlert,
  DollarSign,
  Clock,
  Paperclip,
  FileText,
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { useDeleteHotspot } from '../hooks/hooks';
import FABActionDialog from './FABActionDialog';
import { PhotoMarker } from '../types/types';
import { toast } from '@/shared/hooks/use-toast';
import {
  usePunchListItems,
  usePunchListItem,
  STATUS_LABELS as PUNCH_STATUS_LABELS,
  PRIORITY_LABELS as PUNCH_PRIORITY_LABELS,
  IMPACT_LABELS,
} from '@/features/punch-lists';
import type { PunchListItemSummary, PunchListItem } from '@/features/punch-lists';
import {
  useObservations,
  useObservation,
  STATUS_LABELS as OBS_STATUS_LABELS,
  PRIORITY_LABELS as OBS_PRIORITY_LABELS,
} from '@/features/observations';
import type { ObservationSummary } from '@/features/observations';
import {
  useRFI,
  useRFIs,
  RFI_STATUS_LABELS,
  RFI_PRIORITY_LABELS,
} from '@/features/rfis';
import type { RFISummary } from '@/features/rfis';
import { AttachmentPhotoGallery } from '@/shared/components/AttachmentPhotoGallery';
import { useSafetyIssues } from '@/features/safety';
import { FEATURE_FLAGS } from '@/features/featureFlags';
import { useFeatureAccess } from '@/shared/hooks/useFeatureAccess';

interface HotspotInfo {
  id: number;
  pitch: number;
  yaw: number;
  hfov?: number;
  photo_id: number;
  title?: string;
  created_at?: string;
  updated_at?: string;
  overlay_count?: number;
  created_by?: string | null;
  objects?: unknown[];
}

interface HotspotDialogProps {
  open: boolean;
  onClose: () => void;
  hotspot: HotspotInfo | null;
  onObjectUpdated?: () => Promise<void>;
  onObjectDeleted?: (objectId: number, objectType: string) => Promise<void>;
  projectId?: string;
  markerNumber?: number;
  layoutImageUrl?: string;
  layoutFlippedHorizontal?: boolean;
  layoutFlippedVertical?: boolean;
  layoutImageDimensions?: { width: number; height: number };
  photoMarkers?: PhotoMarker[];
}

const STATUS_COLORS: Record<string, string> = {
  OPEN: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  COMPLETED: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  OVERDUE: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  CLOSED: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300',
};

const PRIORITY_COLORS: Record<string, string> = {
  HIGH: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  MEDIUM: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  LOW: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
};

const RFI_STATUS_COLORS: Record<string, string> = {
  DRAFT: 'bg-gray-100 text-gray-700 dark:bg-gray-800/50 dark:text-gray-300',
  OPEN: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  PENDING_RESPONSE: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  ANSWERED: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  CLOSED: 'bg-slate-100 text-slate-700 dark:bg-slate-900/30 dark:text-slate-300',
};

const RFI_PRIORITY_COLORS: Record<string, string> = {
  LOW: 'bg-slate-100 text-slate-700 dark:bg-slate-800/50 dark:text-slate-300',
  NORMAL: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  HIGH: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  URGENT: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
};

const userDisplayName = (u: { first_name: string; last_name: string; email: string }) =>
  `${u.first_name} ${u.last_name}`.trim() || u.email;

const DetailRow: FC<{ icon: React.ReactNode; label: string; children: React.ReactNode }> = ({
  icon, label, children,
}) => (
  <div className="flex items-start gap-2 text-sm">
    <span className="text-muted-foreground mt-0.5 flex-shrink-0">{icon}</span>
    <div className="min-w-0">
      <span className="text-muted-foreground text-xs">{label}</span>
      <div className="font-medium text-foreground">{children}</div>
    </div>
  </div>
);

const ExpandedItemDetails: FC<{
  summary: PunchListItemSummary;
  projectId: number | undefined;
  onViewDetails: () => void;
}> = ({ summary, projectId, onViewDetails }) => {
  const { data: item, isLoading } = usePunchListItem(summary.id, projectId);

  if (isLoading) {
    return (
      <div className="px-3 pb-3 pt-2 border-t flex items-center justify-center py-4">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const detail: PunchListItem | null = item ?? null;

  return (
    <div className="px-3 pb-3 pt-2 border-t space-y-3">
      {/* Top row: key metadata */}
      <div className="grid grid-cols-2 gap-x-4 gap-y-2.5">
        <DetailRow icon={<AlertCircle className="h-3.5 w-3.5" />} label="Priority">
          <Badge variant="outline" className={`text-xs ${PRIORITY_COLORS[summary.priority] ?? ''}`}>
            {PUNCH_PRIORITY_LABELS[summary.priority] ?? summary.priority}
          </Badge>
        </DetailRow>

        <DetailRow icon={<User className="h-3.5 w-3.5" />} label="Assignee">
          {summary.assignee ? userDisplayName(summary.assignee) : <span className="text-muted-foreground">Unassigned</span>}
        </DetailRow>

        {summary.due_date && (
          <DetailRow icon={<Calendar className="h-3.5 w-3.5" />} label="Due Date">
            {format(new Date(summary.due_date), 'MMM d, yyyy')}
          </DetailRow>
        )}

        {summary.item_type && (
          <DetailRow icon={<Tag className="h-3.5 w-3.5" />} label="Type">
            {summary.item_type.name}
          </DetailRow>
        )}

        {summary.location && (
          <DetailRow icon={<MapPin className="h-3.5 w-3.5" />} label="Location">
            {summary.location.name}
          </DetailRow>
        )}

        {detail?.trade && (
          <DetailRow icon={<Wrench className="h-3.5 w-3.5" />} label="Trade">
            {detail.trade.name}
          </DetailRow>
        )}
      </div>

      {/* Description */}
      {detail?.description && (
        <div className="space-y-1">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <FileText className="h-3 w-3" />
            Description
          </div>
          <p className="text-sm text-foreground line-clamp-3 whitespace-pre-wrap">
            {detail.description}
          </p>
        </div>
      )}

      {/* People + impacts row (from full detail) */}
      {detail && (
        <div className="grid grid-cols-2 gap-x-4 gap-y-2.5">
          {detail.punch_manager && (
            <DetailRow icon={<User className="h-3.5 w-3.5" />} label="Punch Manager">
              {userDisplayName(detail.punch_manager)}
            </DetailRow>
          )}

          {detail.final_approver && (
            <DetailRow icon={<User className="h-3.5 w-3.5" />} label="Final Approver">
              {userDisplayName(detail.final_approver)}
            </DetailRow>
          )}

          {detail.cost_impact && detail.cost_impact !== 'NA' && (
            <DetailRow icon={<DollarSign className="h-3.5 w-3.5" />} label="Cost Impact">
              {IMPACT_LABELS[detail.cost_impact]}
            </DetailRow>
          )}

          {detail.schedule_impact && detail.schedule_impact !== 'NA' && (
            <DetailRow icon={<Clock className="h-3.5 w-3.5" />} label="Schedule Impact">
              {IMPACT_LABELS[detail.schedule_impact]}
            </DetailRow>
          )}

          {detail.cost_code && (
            <DetailRow icon={<DollarSign className="h-3.5 w-3.5" />} label="Cost Code">
              {detail.cost_code.code} &ndash; {detail.cost_code.name}
            </DetailRow>
          )}

          {detail.attachments.length > 0 && (
            <DetailRow icon={<Paperclip className="h-3.5 w-3.5" />} label="Attachments">
              {detail.attachments.length} file{detail.attachments.length !== 1 ? 's' : ''}
            </DetailRow>
          )}
        </div>
      )}

      {(detail?.attachments?.length ?? 0) > 0 && (
        <>
          <AttachmentPhotoGallery
            attachments={detail?.attachments.filter((attachment) => attachment.kind === 'before') ?? []}
            label="Before photos"
            maxVisible={4}
          />
          <AttachmentPhotoGallery
            attachments={detail?.attachments.filter(
              (attachment) => attachment.kind === 'after' || attachment.kind === 'proof'
            ) ?? []}
            label="After photos"
            maxVisible={4}
          />
          <AttachmentPhotoGallery
            attachments={detail?.attachments.filter((attachment) => attachment.kind === 'general') ?? []}
            label="Other photos"
            maxVisible={4}
          />
        </>
      )}

      {/* View Full Details button */}
      <div className="pt-1">
        <Button
          variant="outline"
          size="sm"
          className="w-full gap-1.5 text-xs"
          onClick={(e) => {
            e.stopPropagation();
            onViewDetails();
          }}
        >
          <ExternalLink className="h-3.5 w-3.5" />
          View Full Details
        </Button>
      </div>
    </div>
  );
};

const PunchItemCard: FC<{
  item: PunchListItemSummary;
  expanded: boolean;
  onToggle: () => void;
  onViewDetails: () => void;
  projectId: number | undefined;
}> = ({ item, expanded, onToggle, onViewDetails, projectId }) => (
  <div className="border rounded-lg overflow-hidden">
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center justify-between p-3 hover:bg-accent transition-colors text-left"
    >
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <ClipboardList className="h-4 w-4 text-blue-500 flex-shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            {item.is_private && <Lock className="h-3 w-3 text-muted-foreground flex-shrink-0" />}
            <h4 className="text-sm font-medium truncate">{item.title}</h4>
          </div>
          <p className="text-xs text-muted-foreground">
            {formatDistanceToNow(new Date(item.created_at), { addSuffix: true })}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0 ml-2">
        <Badge variant="secondary" className={`text-xs ${STATUS_COLORS[item.status] ?? ''}`}>
          {PUNCH_STATUS_LABELS[item.status] ?? item.status}
        </Badge>
        {expanded ? (
          <ChevronUp className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        )}
      </div>
    </button>

    {expanded && (
      <ExpandedItemDetails
        summary={item}
        projectId={projectId}
        onViewDetails={onViewDetails}
      />
    )}
  </div>
);

const ObservationCard: FC<{
  item: ObservationSummary;
  expanded: boolean;
  onToggle: () => void;
  onViewDetails: () => void;
  projectId: number | undefined;
}> = ({ item, expanded, onToggle, onViewDetails, projectId }) => {
  const { data: detail, isLoading: detailLoading } = useObservation(
    expanded ? item.id : undefined,
    projectId,
  );

  return (
    <div className="border rounded-lg overflow-hidden border-amber-200/50 dark:border-amber-800/30">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between p-3 hover:bg-accent transition-colors text-left"
      >
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <Eye className="h-4 w-4 text-amber-500 flex-shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              {item.is_private && <Lock className="h-3 w-3 text-muted-foreground flex-shrink-0" />}
              <span className="text-xs text-muted-foreground font-mono">OBS-{item.number}</span>
              <h4 className="text-sm font-medium truncate">{item.title}</h4>
            </div>
            <p className="text-xs text-muted-foreground">
              {formatDistanceToNow(new Date(item.created_at), { addSuffix: true })}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 ml-2">
          <Badge variant="secondary" className={`text-xs ${STATUS_COLORS[item.status] ?? ''}`}>
            {OBS_STATUS_LABELS[item.status] ?? item.status}
          </Badge>
          {expanded ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
        </div>
      </button>

      {expanded && (
        <div className="px-3 pb-3 pt-2 border-t space-y-3">
          {detailLoading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2.5">
                <DetailRow icon={<AlertCircle className="h-3.5 w-3.5" />} label="Priority">
                  <Badge variant="outline" className={`text-xs ${PRIORITY_COLORS[item.priority] ?? ''}`}>
                    {OBS_PRIORITY_LABELS[item.priority] ?? item.priority}
                  </Badge>
                </DetailRow>
                <DetailRow icon={<User className="h-3.5 w-3.5" />} label="Assignee">
                  {item.assignee ? userDisplayName(item.assignee) : <span className="text-muted-foreground">Unassigned</span>}
                </DetailRow>
                {item.due_date && (
                  <DetailRow icon={<Calendar className="h-3.5 w-3.5" />} label="Due Date">
                    {format(new Date(item.due_date), 'MMM d, yyyy')}
                  </DetailRow>
                )}
                {item.observation_type && (
                  <DetailRow icon={<Tag className="h-3.5 w-3.5" />} label="Type">
                    {item.observation_type.name}
                  </DetailRow>
                )}
                {item.location && (
                  <DetailRow icon={<MapPin className="h-3.5 w-3.5" />} label="Location">
                    {item.location.name}
                  </DetailRow>
                )}
                {detail?.trade && (
                  <DetailRow icon={<Wrench className="h-3.5 w-3.5" />} label="Trade">
                    {detail.trade.name}
                  </DetailRow>
                )}
              </div>
              {detail?.description && (
                <div className="space-y-1">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <FileText className="h-3 w-3" /> Description
                  </div>
                  <p className="text-sm text-foreground line-clamp-3 whitespace-pre-wrap">{detail.description}</p>
                </div>
              )}
              {(detail?.attachments?.length ?? 0) > 0 && (
                <AttachmentPhotoGallery attachments={detail?.attachments ?? []} maxVisible={4} />
              )}
            </>
          )}
          <div className="pt-1">
            <Button variant="outline" size="sm" className="w-full gap-1.5 text-xs" onClick={(e) => { e.stopPropagation(); onViewDetails(); }}>
              <ExternalLink className="h-3.5 w-3.5" /> View Full Details
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

const RfiCard: FC<{
  item: RFISummary;
  expanded: boolean;
  onToggle: () => void;
  onViewDetails: () => void;
  projectId: number | undefined;
}> = ({ item, expanded, onToggle, onViewDetails, projectId }) => {
  const { data: detail, isLoading: detailLoading } = useRFI(
    expanded ? item.id : undefined,
    projectId,
  );

  return (
    <div className="border rounded-lg overflow-hidden border-purple-200/50 dark:border-purple-800/30">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between p-3 hover:bg-accent transition-colors text-left"
      >
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <HelpCircle className="h-4 w-4 text-purple-500 flex-shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-muted-foreground font-mono">{item.rfi_number}</span>
              <h4 className="text-sm font-medium truncate">{item.title}</h4>
            </div>
            <p className="text-xs text-muted-foreground">
              {formatDistanceToNow(new Date(item.created_at), { addSuffix: true })}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 ml-2">
          <Badge variant="secondary" className={`text-xs ${RFI_STATUS_COLORS[item.status] ?? ''}`}>
            {RFI_STATUS_LABELS[item.status] ?? item.status}
          </Badge>
          {expanded ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
        </div>
      </button>

      {expanded && (
        <div className="px-3 pb-3 pt-2 border-t space-y-3">
          {detailLoading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2.5">
                <DetailRow icon={<AlertCircle className="h-3.5 w-3.5" />} label="Priority">
                  <Badge variant="outline" className={`text-xs ${RFI_PRIORITY_COLORS[item.priority] ?? ''}`}>
                    {RFI_PRIORITY_LABELS[item.priority] ?? item.priority}
                  </Badge>
                </DetailRow>
                <DetailRow icon={<User className="h-3.5 w-3.5" />} label="Responsible Party">
                  {item.responsible_party_user
                    ? userDisplayName(item.responsible_party_user)
                    : <span className="text-muted-foreground">Unassigned</span>}
                </DetailRow>
                {item.assignee_user && (
                  <DetailRow icon={<User className="h-3.5 w-3.5" />} label="Assignee">
                    {userDisplayName(item.assignee_user)}
                  </DetailRow>
                )}
                {item.due_date && (
                  <DetailRow icon={<Calendar className="h-3.5 w-3.5" />} label="Due Date">
                    {format(new Date(item.due_date), 'MMM d, yyyy')}
                  </DetailRow>
                )}
                {item.trade && (
                  <DetailRow icon={<Wrench className="h-3.5 w-3.5" />} label="Trade">
                    {item.trade.name}
                  </DetailRow>
                )}
                {detail?.location_reference && (
                  <DetailRow icon={<MapPin className="h-3.5 w-3.5" />} label="Location">
                    {detail.location_reference.name}
                  </DetailRow>
                )}
                {detail?.drawing_reference && (
                  <DetailRow icon={<FileText className="h-3.5 w-3.5" />} label="Drawing">
                    {detail.drawing_reference.label}
                  </DetailRow>
                )}
                {detail && detail.cost_impact !== 'NONE' && (
                  <DetailRow icon={<DollarSign className="h-3.5 w-3.5" />} label="Cost Impact">
                    {detail.cost_impact}
                  </DetailRow>
                )}
                {detail && detail.schedule_impact !== 'NONE' && (
                  <DetailRow icon={<Clock className="h-3.5 w-3.5" />} label="Schedule Impact">
                    {detail.schedule_impact}
                  </DetailRow>
                )}
              </div>
              {detail?.question && (
                <div className="space-y-1">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <FileText className="h-3 w-3" /> Question
                  </div>
                  <p className="text-sm text-foreground line-clamp-3 whitespace-pre-wrap">{detail.question}</p>
                </div>
              )}
              {(detail?.attachments?.length ?? 0) > 0 && (
                <DetailRow icon={<Paperclip className="h-3.5 w-3.5" />} label="Attachments">
                  {detail?.attachments.length} file{(detail?.attachments.length ?? 0) !== 1 ? 's' : ''}
                </DetailRow>
              )}
            </>
          )}
          <div className="pt-1">
            <Button variant="outline" size="sm" className="w-full gap-1.5 text-xs" onClick={(e) => { e.stopPropagation(); onViewDetails(); }}>
              <ExternalLink className="h-3.5 w-3.5" /> View Full Details
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

const HotspotDialog: FC<HotspotDialogProps> = ({
  open,
  onClose,
  hotspot,
  onObjectUpdated,
  projectId,
}) => {
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showDeleteHotspotDialog, setShowDeleteHotspotDialog] = useState(false);
  const [expandedItemId, setExpandedItemId] = useState<string | null>(null);

  const navigate = useNavigate();
  const location = useLocation();

  const numericProjectId = projectId ? Number(projectId) : undefined;
  const scopedProjectId = hotspot ? numericProjectId : undefined;
  const safetyAccess = useFeatureAccess(FEATURE_FLAGS.SAFETY);

  const { data: punchItems, isLoading: punchLoading, refetch: refetchPunch } = usePunchListItems(
    scopedProjectId,
    hotspot ? { hotspot_id: hotspot.id, photo_id: hotspot.photo_id } : {},
  );

  const { data: observations, isLoading: obsLoading, refetch: refetchObs } = useObservations(
    scopedProjectId,
    hotspot ? { hotspot_id: hotspot.id, photo_id: hotspot.photo_id } : {},
  );

  const { data: rfis, isLoading: rfisLoading, refetch: refetchRfis } = useRFIs(
    scopedProjectId,
    hotspot ? { hotspot_id: hotspot.id, photo_id: hotspot.photo_id } : {},
  );

  const { data: safetyIssues, isLoading: safetyLoading, refetch: refetchSafety } = useSafetyIssues(
    safetyAccess.isEnabled ? scopedProjectId : undefined,
    hotspot ? { hotspotId: hotspot.id } : {},
  );

  const isLoading = punchLoading || obsLoading || rfisLoading || safetyLoading;
  const refetch = useCallback(
    () => {
      refetchPunch();
      refetchObs();
      refetchRfis();
      if (safetyAccess.isEnabled) refetchSafety();
    },
    [refetchPunch, refetchObs, refetchRfis, refetchSafety, safetyAccess.isEnabled],
  );

  const deleteHotspotMutation = useDeleteHotspot();

  const getBasePath = useCallback(
    () => location.pathname.replace(/\/(asbuilds|progress)(?:\/.*)?$/, ''),
    [location.pathname],
  );

  const handleViewPunchDetail = useCallback(
    (itemId: string) => {
      onClose();
      navigate(`${getBasePath()}/punch-lists/${itemId}`);
    },
    [navigate, getBasePath, onClose],
  );

  const handleViewObservationDetail = useCallback(
    (itemId: string) => {
      onClose();
      navigate(`${getBasePath()}/observations/${itemId}`);
    },
    [navigate, getBasePath, onClose],
  );

  const handleViewRfiDetail = useCallback(
    (itemId: string) => {
      onClose();
      navigate(`${getBasePath()}/rfis/${itemId}`);
    },
    [navigate, getBasePath, onClose],
  );

  const handleDeleteHotspot = useCallback(async () => {
    if (!hotspot || !projectId) return;
    try {
      await deleteHotspotMutation.mutateAsync({
        hotspotId: hotspot.id,
        projectId,
      });
      toast({ title: 'Hotspot deleted', variant: 'default', className: 'bg-green-500 text-white' });
      setShowDeleteHotspotDialog(false);
      onClose();
    } catch {
      toast({ title: 'Failed to delete hotspot', variant: 'destructive' });
    }
  }, [hotspot, projectId, deleteHotspotMutation, onClose]);

  const handleAddSuccess = useCallback(async () => {
    setShowAddDialog(false);
    await onObjectUpdated?.();
    refetch();
  }, [onObjectUpdated, refetch]);

  const handleDialogClose = useCallback(() => {
    setExpandedItemId(null);
    setShowAddDialog(false);
    onClose();
  }, [onClose]);

  if (!hotspot) return null;

  const punchList = punchItems ?? [];
  const obsList = observations ?? [];
  const rfiList = rfis ?? [];
  const safetyList = safetyAccess.isEnabled ? safetyIssues ?? [] : [];
  const totalItems = punchList.length + obsList.length + rfiList.length + safetyList.length;
  const isEmpty = !isLoading && totalItems === 0;

  return (
    <>
      <Dialog open={open} onOpenChange={handleDialogClose}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-hidden flex flex-col bg-popover">
          <DialogHeader className="flex-shrink-0 py-6 border-b">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-blue-500/10 flex items-center justify-center">
                  <ClipboardList className="h-4 w-4 text-blue-500" />
                </div>
                <DialogTitle className="text-lg font-semibold">Hotspot Items</DialogTitle>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="gap-1"
                onClick={() => setShowAddDialog(true)}
              >
                <Plus className="h-3.5 w-3.5" />
                Add Item
              </Button>
            </div>
            <DialogDescription className="sr-only">
              View and manage items pinned to this location
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto py-3 space-y-2">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : isEmpty ? (
              <div className="text-center py-12 space-y-3">
                <ClipboardList className="h-10 w-10 mx-auto text-muted-foreground/50" />
                <div>
                  <p className="text-sm text-muted-foreground">No items attached to this hotspot.</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Click "Add Item" to pin a punch item, observation, RFI, or safety issue to this location.
                  </p>
                </div>
                <div className="pt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowDeleteHotspotDialog(true)}
                    className="text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/20"
                    disabled={deleteHotspotMutation.isPending}
                  >
                    {deleteHotspotMutation.isPending ? (
                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                    ) : (
                      <Trash2 className="h-3 w-3 mr-1" />
                    )}
                    Delete Hotspot
                  </Button>
                </div>
              </div>
            ) : (
              <>
                {punchList.map((item) => (
                  <PunchItemCard
                    key={`punch-${item.id}`}
                    item={item}
                    expanded={expandedItemId === `punch-${item.id}`}
                    onToggle={() =>
                      setExpandedItemId((prev) => (prev === `punch-${item.id}` ? null : `punch-${item.id}`))
                    }
                    onViewDetails={() => handleViewPunchDetail(item.id)}
                    projectId={numericProjectId}
                  />
                ))}
                {obsList.map((item) => (
                  <ObservationCard
                    key={`obs-${item.id}`}
                    item={item}
                    expanded={expandedItemId === `obs-${item.id}`}
                    onToggle={() =>
                      setExpandedItemId((prev) => (prev === `obs-${item.id}` ? null : `obs-${item.id}`))
                    }
                    onViewDetails={() => handleViewObservationDetail(item.id)}
                    projectId={numericProjectId}
                  />
                ))}
                {rfiList.map((item) => (
                  <RfiCard
                    key={`rfi-${item.id}`}
                    item={item}
                    expanded={expandedItemId === `rfi-${item.id}`}
                    onToggle={() =>
                      setExpandedItemId((prev) => (prev === `rfi-${item.id}` ? null : `rfi-${item.id}`))
                    }
                    onViewDetails={() => handleViewRfiDetail(item.id)}
                    projectId={numericProjectId}
                  />
                ))}
                {safetyList.map((item) => (
                  <button
                    key={`safety-${item.id}`}
                    type="button"
                    className="flex w-full items-start gap-3 rounded-lg border border-destructive/20 p-3 text-left transition-colors hover:bg-accent"
                    onClick={() => {
                      onClose();
                      navigate(`${getBasePath()}/safety`);
                    }}
                  >
                    <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-destructive/10 text-destructive">
                      <ShieldAlert className="size-4" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="line-clamp-2 text-sm font-medium">{item.description}</span>
                      <span className="mt-1 block text-xs text-muted-foreground">
                        Safety issue · {formatDistanceToNow(new Date(item.created_at), { addSuffix: true })}
                      </span>
                    </span>
                    <Badge variant={item.status === 'OPEN' ? 'destructive' : 'secondary'}>
                      {item.status === 'OPEN' ? 'Open' : 'Resolved'}
                    </Badge>
                  </button>
                ))}
              </>
            )}
          </div>

          {!isEmpty && (
            <div className="flex-shrink-0 pt-2 border-t flex justify-between items-center">
              <p className="text-xs text-muted-foreground">
                {totalItems} item{totalItems !== 1 ? 's' : ''} attached
              </p>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowDeleteHotspotDialog(true)}
                className="text-destructive hover:text-destructive text-xs"
                disabled={deleteHotspotMutation.isPending}
              >
                <Trash2 className="h-3 w-3 mr-1" />
                Delete Hotspot
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showDeleteHotspotDialog} onOpenChange={setShowDeleteHotspotDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete hotspot?</DialogTitle>
            <DialogDescription>
              This will delete the hotspot and unlink all attached items. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowDeleteHotspotDialog(false)} disabled={deleteHotspotMutation.isPending}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteHotspot} disabled={deleteHotspotMutation.isPending}>
              {deleteHotspotMutation.isPending ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete hotspot'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <FABActionDialog
        open={showAddDialog}
        setOpen={setShowAddDialog}
        photoId={hotspot.photo_id}
        projectId={projectId}
        viewPosition={{
          pitch: hotspot.pitch,
          yaw: hotspot.yaw,
          hfov: hotspot.hfov ?? 100,
        }}
        hotspotId={hotspot.id}
        onSuccess={handleAddSuccess}
        onHotspotCreated={async () => {
          await onObjectUpdated?.();
          refetch();
        }}
      />
    </>
  );
};

export default HotspotDialog;
